import { Chip } from '@mantine/core';
import { Close } from '@carbon/icons-react';
import { defineMessages, useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';

import styles from './WeekDayPicker.module.scss';

import { WeekDay } from 'Components/Common/WeekDayPicker/constants';

export type SelectedDays = {
    Monday: boolean;
    Tuesday: boolean;
    Wednesday: boolean;
    Thursday: boolean;
    Friday: boolean;
    Saturday: boolean;
    Sunday: boolean;
};

type Props = {
    onValueChange: (values: SelectedDays) => void;
    selectedDays?: SelectedDays;
    allowEmpty?: boolean;
    allowAll?: boolean;
    excludedDaysMode?: boolean;
}

const WeekDayPicker = ({
    onValueChange,
    selectedDays = {
        [WeekDay.Monday]: true,
        [WeekDay.Tuesday]: false,
        [WeekDay.Wednesday]: false,
        [WeekDay.Thursday]: false,
        [WeekDay.Friday]: false,
        [WeekDay.Saturday]: false,
        [WeekDay.Sunday]: false,
    },
    allowEmpty = false,
    allowAll = true,
    excludedDaysMode = false,
}: Props) => {
    const { formatMessage } = useIntl();
    // const [selectedDays, setSelectedDays] = useState(initialValue);

    const isOnlyOneDaySelected = useMemo(() => Object.values(selectedDays).filter((value) => value).length === 1, [selectedDays]);

    const getOnValueChangeMethod = useCallback((day: WeekDay) => (value: boolean) => {
        if (!allowEmpty && isOnlyOneDaySelected && selectedDays[day]) {
            return;
        }

        const newSelectedDays = {
            ...selectedDays,
            [day]: value,
        };
        if (!allowAll && Object.values(newSelectedDays).every(Boolean)) {
            return;
        }
        onValueChange(newSelectedDays);
    }, [allowAll, allowEmpty, isOnlyOneDaySelected, onValueChange, selectedDays]);

    return (
        <div className={styles.container}>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Monday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Monday)}
                variant="outline"
            >
                {formatMessage(messages.monday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Tuesday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Tuesday)}
                variant="outline"
            >
                {formatMessage(messages.tuesday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Wednesday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Wednesday)}
                variant="outline"
            >
                {formatMessage(messages.wednesday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Thursday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Thursday)}
                variant="outline"
            >
                {formatMessage(messages.thursday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Friday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Friday)}
                variant="outline"
            >
                {formatMessage(messages.friday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Saturday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Saturday)}
                variant="outline"
            >
                {formatMessage(messages.saturday)}
            </Chip>
            <Chip
                size="xs"
                checked={selectedDays[WeekDay.Sunday]}
                className={styles.chip}
                classNames={excludedDaysMode ? { label: styles.excludedDaysLabel } : undefined}
                color={excludedDaysMode ? 'red' : undefined}
                icon={excludedDaysMode ? <Close size={12} /> : undefined}
                onChange={getOnValueChangeMethod(WeekDay.Sunday)}
                variant="outline"
            >
                {formatMessage(messages.sunday)}
            </Chip>
        </div>
    );
};

const messages = defineMessages({
    monday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.monday',
        defaultMessage: 'pn',
    },
    tuesday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.tuesday',
        defaultMessage: 'wt',
    },
    wednesday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.wednesday',
        defaultMessage: 'śr',
    },
    thursday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.thursday',
        defaultMessage: 'cz',
    },
    friday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.friday',
        defaultMessage: 'pt',
    },
    saturday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.saturday',
        defaultMessage: 'sb',
    },
    sunday: {
        id: 'src.Components.Common.WeekDayPicker.WeekDayPicker.sunday',
        defaultMessage: 'nd',
    },
});

export default WeekDayPicker;
